import {Injectable} from "@angular/core";
import {Headers, Http, RequestOptionsArgs, Response} from "@angular/http";
import {Storage} from "@ionic/storage";
import {AlertController, App} from "ionic-angular";
import "rxjs/add/observable/fromPromise";
import "rxjs/add/observable/throw";
import "rxjs/add/operator/catch";
import "rxjs/add/operator/map";
import "rxjs/add/operator/mergeMap";
import "rxjs/add/operator/publishReplay";
import "rxjs/add/operator/toPromise";
import {Observable} from "rxjs/Observable";
import {ReplaySubject} from "rxjs/ReplaySubject";
import {ServiceConfig} from "../app/services-config";
import {HttpService} from "./http.service";

@Injectable()
export class AuthorizedService extends HttpService {
  private refreshRequest: Observable<any>;
  private _statusChange: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);
  private _token: any;

  constructor(private storage: Storage,
              http: Http,
              app: App,
              alertCtrl: AlertController) {
    super(http, app, alertCtrl);

    this.storage.get('token').then((value) => {
      this.token = value || null;
    }, () => {
      this.token = null;
    });
  }

  private redirectToLogin() {
    setTimeout(() => {
      if (this.appConfig.global_login) {
        this.app.getRootNavs()[0].setRoot('Login');
      } else {
        this.app.getRootNavs()[0].setPages([{page: 'HomePage'}, {page: 'Login'}]);
      }
    });
  }

  get isLoggedIn() {
    return !!this._token;
  }

  get token() {
    return this._token;
  }

  set token(value: any) {
    this._token = value;
    this._statusChange.next(!!value);

    if (value) {
      this.storage.set('token', value);
    } else {
      this.storage.remove('token');
    }
  }

  get statusChange() {
    return this._statusChange.asObservable();
  }

  public hasGlobalLogin(): Observable<boolean> {
    if (!this.appConfig) {
      return this.loadAppConfig().map((appConfig) => appConfig.global_login);
    }

    return Observable.of(this.appConfig.global_login);
  }

  protected request(url: string, options: RequestOptionsArgs, retry: boolean = false): Observable<Response> {
    if (this.token) {
      options.headers = options.headers || new Headers();
      options.headers.set('Authorization', `Bearer ${this.token}`);
      options.withCredentials = true;
    }

    return super.request(url, options)
      .catch((error) => {
        if (error.status === 401) {
          if (!retry) {
            if (!this.token) {
              this.redirectToLogin();
              return Observable.throw(error);
            }

            if (!this.refreshRequest) {
              this.refreshRequest = this.http.post(ServiceConfig.baseUrl + ServiceConfig.refreshUrl, null, {headers: options.headers})
                .catch(() => {
                  this.logout(false);
                  this.redirectToLogin();

                  return Observable.throw(error);
                })
                .map(response => response.json())
                .mergeMap(response => {
                  this.refreshRequest = null;

                  return Observable.fromPromise(this.storage.set('token', response.token).then(() => {
                    this.token = response.token;
                    return response;
                  }));
                })
                .finally(() => {
                  this.refreshRequest = null;
                })
                .publishReplay(1)
                .refCount();
            }

            return this.refreshRequest.mergeMap(() => {
              return this.request(url, options, true);
            });
          }

          this.redirectToLogin();
        }

        return Observable.throw(error);
      });
  }

  public login(credentials) {
    let headers = new Headers();
    headers.set('Content-Type', 'application/json');

    return this.http.post(ServiceConfig.baseUrl + ServiceConfig.loginUrl, credentials, {headers})
      .map((response) => {
        let data = response.json();
        this.token = data.token;
        return data;
      });
  }

  public logout(redirect: boolean = true) {
    if (!this.token) {
      return Observable.of(true);
    }

    let headers = new Headers();
    headers.set('Content-Type', 'application/json');
    headers.set('Authorization', `Bearer ${this.token}`);

    return this.http.post(ServiceConfig.baseUrl + ServiceConfig.logoutUrl, null, {headers})
      .catch((error) => {
        if (error instanceof Response && error.status === 401) {
          this.token = null;
          return Observable.of(true);
        }

        return Observable.throw(error);
      })
      .map(() => {
        this.token = null;

        if (redirect) {
          this.redirectToLogin();
        }

        return true;
      });
  }

  public can(ability: string, type: string, id?: number): Observable<any> {
    if (!this.token) {
      return Observable.of(false);
    }

    return this.post(ServiceConfig.canUrl, {ability, type, id}).map(response => response.json());
  }

  public password(credentials) {
    return this.post(ServiceConfig.passwordUrl, credentials).map(response => response.json());
  }

  public forgot({email}: { email: string }) {
    return this.post(ServiceConfig.forgotUrl, {email}).map(response => response.json());
  }

  public user(): Observable<any> {
    return this.get(ServiceConfig.userUrl).map(response => response.json());
  }
}



// WEBPACK FOOTER //
// ./src/services/authorized.service.ts